/**
 * MyCopro - Types Demandes
 * Basé sur le prototype V6 (lignes 251-350)
 * ✅ MISE À JOUR: Champs spécifiques étiquettes BAL (nom1, nom2, numBAL, labelFormat)
 * ✅ MISE À JOUR: Support réouverture de demande clôturée (reopenedBy, etc.)
 * ✅ MISE À JOUR: Interface LabelFormat pour paramètres résidence
 * ✅ MISE À JOUR: labelPdfUrl — URL du PDF généré stocké dans Firebase Storage,
 *    accessible par le demandeur depuis la page détail de sa demande
 */

import { LucideIcon } from 'lucide-react';
import { UserCategoryType } from './user';

// ============================================================================
// TYPES DE DEMANDES
// ============================================================================

export type RequestType = 'label' | 'fiber' | 'problem';

export interface RequestTypeConfig {
  title: string;
  description: string;
  color: string;
  icon: LucideIcon;
}

export type RequestTypesMap = Record<RequestType, RequestTypeConfig>;

// ============================================================================
// STATUTS DE DEMANDE
// ============================================================================

export type RequestStatus = 'pending' | 'validated' | 'transmitted' | 'rejected' | 'closed' | 'cancelled';

export interface RequestStatusConfig {
  label: string;
  color: string;
  icon: LucideIcon;
}

export type RequestStatusMap = Record<RequestStatus, RequestStatusConfig>;

// ============================================================================
// VISIBILITÉ DE DEMANDE
// ============================================================================

export type RequestVisibility = 'public' | 'private';

// ============================================================================
// DESTINATAIRE TRANSMISSION
// ============================================================================

export type TransmissionTarget = 'syndic' | 'bailleur';

// ============================================================================
// ✅ NOUVEAU: FORMAT D'ÉTIQUETTE BAL (sauvegardé au niveau résidence)
// Stocké dans Firestore: residences/{id}/settings/labelFormat
// Partagé entre tous les membres CS/Syndic de la résidence
// ============================================================================

export type LabelNumDisplay = 'apt' | 'bal' | 'both';
export type LabelTextAlign = 'left' | 'center' | 'right';
export type LabelNumPosition = 'left' | 'right';

export interface LabelFormat {
  darkMode: boolean;           // false = texte noir/fond blanc, true = inverse
  textAlign: LabelTextAlign;   // Alignement des noms sur l'étiquette
  aptPosition: LabelNumPosition; // Position du numéro (gauche ou droite)
  numDisplay: LabelNumDisplay; // Quel numéro afficher: apt seul, BAL seul, ou les deux
  // ✅ NOUVEAU: Mode self-service — le résident génère lui-même son étiquette PDF
  // Activable uniquement si un format a été sauvegardé pour la résidence
  selfService: boolean;
}

export const DEFAULT_LABEL_FORMAT: LabelFormat = {
  darkMode: false,
  textAlign: 'center',
  aptPosition: 'left',
  numDisplay: 'apt',
  selfService: false              // Par défaut désactivé — le CS doit l'activer explicitement
};

// ============================================================================
// ✅ NOUVEAU: CHAMPS SPÉCIFIQUES ÉTIQUETTE BAL
// Stockés dans le champ labelData de la demande Firestore
// ============================================================================

export interface LabelFields {
  nom1: string;           // NOM Prénom ligne 1 (obligatoire)
  nom2?: string;          // NOM Prénom ligne 2 (optionnel, pour 2e occupant)
  numBAL?: string;        // Numéro boîte aux lettres (optionnel, en plus du n° apt)
}

// ============================================================================
// DEMANDE COMPLÈTE
// ============================================================================

export interface Request {
  id: string;
  residenceId: string;
  
  // Contenu
  type: RequestType;
  title: string;
  description: string;
  
  // ✅ NOUVEAU: Champs spécifiques étiquette BAL
  // Présent uniquement pour les demandes de type 'label'
  labelData?: LabelFields;
  
  // ✅ NOUVEAU: URL du PDF de l'étiquette générée et stockée dans Firebase Storage
  // Renseigné par le CS/Syndic lors de l'impression, accessible par le demandeur
  labelPdfUrl?: string;
  
  // Soumetteur
  submittedBy: string; // Firebase UID
  submitterName: string;
  submitterCategory: UserCategoryType;
  submitterEmail: string;
  submitterApartment: string;
  
  // Champs legacy / compatibilité avec hooks existants
  submitterIdentifier?: string;
  authorId?: string;
  authorName?: string;
  authorCategory?: string;
  problemType?: 'collective' | 'private' | 'privatif';
  building?: string | null; // Bâtiment concerné pour les demandes collectives ('all' = toute la copropriété)
  photoURL?: string;
  
  // Visibilité
  visibility: RequestVisibility;
  
  // Statut
  status: RequestStatus;
  
  // Photo jointe
  photo: boolean;
  photoUrl?: string; // URL Firebase Storage ou base64
  
  // Dates
  date: string; // ISO date de création
  
  // Validation (si validé)
  validatedBy?: string; // Firebase UID
  validatedByName?: string;
  validatedAt?: string; // ISO date
  
  // Transmission (si transmis)
  transmittedAt?: string; // ISO date
  transmittedTo?: TransmissionTarget;
  bailleurId?: string; // ID du bailleur si transmis au bailleur
  
  // Clôture (si clôturé)
  closedBy?: string; // Firebase UID
  closedByName?: string;
  closedAt?: string; // ISO date
  closedReason?: string;
  
  // ✅ NOUVEAU: Réouverture (si demande clôturée puis réouverte)
  // Permet la modification et réimpression d'une étiquette déjà imprimée
  reopenedBy?: string; // Firebase UID
  reopenedByName?: string;
  reopenedAt?: string; // ISO date
  reopenedReason?: string;
  
  // Refus (si refusé)
  rejectedBy?: string; // Firebase UID
  rejectedByName?: string;
  rejectionReason?: string;
  
  // Numéro de ticket global
  ticketNumber?: string; // Format : TCK-000001

  // Complément CS ajouté lors de la transmission au syndic
  csComment?: string;
  csPhotoURLs?: string[];

  // Métadonnées
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// CRÉATION DE DEMANDE
// ============================================================================

export interface CreateRequestData {
  residenceId: string;
  type: RequestType;
  title: string;
  description: string;
  visibility: RequestVisibility;
  submittedBy: string;
  submitterName: string;
  submitterCategory: UserCategoryType;
  submitterEmail: string;
  submitterApartment: string;
  photoUrl?: string;
  // ✅ NOUVEAU: Champs étiquette BAL
  labelData?: LabelFields;
}

// ============================================================================
// VALIDATION DE DEMANDE
// ============================================================================

export interface ValidateRequestData {
  validatedBy: string;
  validatedByName: string;
  transmitToSyndic: boolean; // true = transmettre, false = clôturer
}

// ============================================================================
// TRANSMISSION DE DEMANDE
// ============================================================================

export interface TransmitRequestData {
  transmittedTo: TransmissionTarget;
  bailleurId?: string;
}

// ============================================================================
// REFUS DE DEMANDE
// ============================================================================

export interface RejectRequestData {
  rejectedBy: string;
  rejectedByName: string;
  rejectionReason: string;
}

// ============================================================================
// CLÔTURE DE DEMANDE
// ============================================================================

export interface CloseRequestData {
  closedBy: string;
  closedByName: string;
  closedReason: string;
}

// ============================================================================
// ✅ NOUVEAU: RÉOUVERTURE DE DEMANDE
// Permet au CS/Syndic/Admin de réouvrir une demande clôturée
// pour modification ou réimpression (ex: étiquette BAL avec erreur)
// ============================================================================

export interface ReopenRequestData {
  reopenedBy: string;
  reopenedByName: string;
  reopenedReason?: string;
}

// ============================================================================
// FORMULAIRE DE DEMANDE
// ============================================================================

export interface RequestFormData {
  type: RequestType | null;
  title: string;
  description: string;
  visibility: RequestVisibility;
  photo: File | null;
  photoPreview: string | null;
  // ✅ NOUVEAU: Champs étiquette BAL dans le formulaire
  labelData?: LabelFields;
}

// ============================================================================
// FILTRES DEMANDES
// ============================================================================

export interface RequestFilters {
  status?: RequestStatus | 'all';
  type?: RequestType | 'all';
  visibility?: RequestVisibility | 'all';
  submittedBy?: string; // Firebase UID
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * ✅ NOUVEAU: Formate un nom pour l'étiquette BAL
 * NOM de famille en MAJUSCULES, Prénom en capitalize (1ère lettre maj, reste min)
 * Heuristique: le dernier mot est le prénom, tout le reste est le nom de famille
 * 
 * Exemples:
 *   "dupont marie"           → "DUPONT Marie"
 *   "de la fontaine jean"    → "DE LA FONTAINE Jean"
 *   "martin"                 → "MARTIN" (un seul mot = nom de famille)
 */
export function formatLabelName(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].toUpperCase();
  // Tous les mots sauf le dernier = nom de famille (MAJUSCULES)
  const surname = parts.slice(0, -1).map(w => w.toUpperCase()).join(' ');
  // Dernier mot = prénom (Capitalize)
  const firstname = parts[parts.length - 1];
  const cap = firstname.charAt(0).toUpperCase() + firstname.slice(1).toLowerCase();
  return `${surname} ${cap}`;
}

/**
 * ✅ NOUVEAU: Calcule le texte du numéro à afficher sur l'étiquette
 * Selon le choix numDisplay: apt seul, BAL seul, ou les deux séparés par " · "
 */
export function getLabelDisplayNumber(
  numAppart: string,
  numBAL: string | undefined,
  numDisplay: LabelNumDisplay
): string {
  const apt = numAppart?.trim() || '';
  const bal = numBAL?.trim() || '';
  if (numDisplay === 'both') return [apt, bal].filter(Boolean).join(' · ');
  if (numDisplay === 'bal') return bal || apt;
  return apt || bal;
}

/**
 * Crée une nouvelle demande avec les valeurs par défaut
 */
export function createRequestFromData(data: CreateRequestData): Omit<Request, 'id'> {
  const now = new Date().toISOString();
  const isPrivate = data.visibility === 'private';
  
  return {
    residenceId: data.residenceId,
    type: data.type,
    title: data.title,
    description: data.description,
    // ✅ NOUVEAU: Données étiquette BAL
    labelData: data.labelData,
    submittedBy: data.submittedBy,
    submitterName: data.submitterName,
    submitterCategory: data.submitterCategory,
    submitterEmail: data.submitterEmail,
    submitterApartment: data.submitterApartment,
    visibility: data.visibility,
    // Les demandes privatives sont directement transmises
    status: isPrivate ? 'transmitted' : 'pending',
    photo: !!data.photoUrl,
    photoUrl: data.photoUrl,
    date: now.split('T')[0],
    transmittedAt: isPrivate ? now.split('T')[0] : undefined,
    transmittedTo: isPrivate ? 'syndic' : undefined,
    createdAt: now,
    updatedAt: now
  };
}

/**
 * Vérifie si un utilisateur peut clôturer une demande
 */
export function canCloseRequest(
  request: Request,
  userUid: string,
  userCategory: UserCategoryType
): boolean {
  // Créateur peut clôturer
  if (request.submittedBy === userUid) return true;
  
  // CS, Syndic, Admin peuvent clôturer
  return ['council', 'syndic', 'admin'].includes(userCategory);
}

/**
 * ✅ NOUVEAU: Vérifie si un utilisateur peut réouvrir une demande clôturée
 * Seuls CS, Syndic et Admin peuvent réouvrir
 * La demande doit être en statut 'closed'
 */
export function canReopenRequest(
  request: Request,
  userCategory: UserCategoryType
): boolean {
  // Seules les demandes clôturées peuvent être réouvertes
  if (request.status !== 'closed') return false;
  
  // CS, Syndic, Admin peuvent réouvrir
  return ['council', 'syndic', 'admin'].includes(userCategory);
}

/**
 * Vérifie si un utilisateur peut voir une demande
 */
export function canViewRequest(
  request: Request,
  userUid: string,
  userCategory: UserCategoryType
): boolean {
  // Créateur voit toujours sa demande
  if (request.submittedBy === userUid) return true;
  
  // Les demandes privatives ne sont visibles que par le créateur et CS/Syndic/Admin
  if (request.visibility === 'private') {
    return ['council', 'syndic', 'admin'].includes(userCategory);
  }
  
  // Les demandes publiques sont visibles par tous
  return true;
}

/**
 * Filtre les demandes selon la catégorie utilisateur
 */
export function filterRequestsForUser(
  requests: Request[],
  userUid: string,
  userCategory: UserCategoryType
): Request[] {
  // Syndic ne voit que les demandes transmises
  if (userCategory === 'syndic') {
    return requests.filter(r => 
      r.status === 'transmitted' || 
      r.status === 'validated' ||
      r.status === 'closed'
    );
  }
  
  // Les autres voient selon leurs permissions
  return requests.filter(r => canViewRequest(r, userUid, userCategory));
}
